import { csx, Flex } from '@vtex/admin-ui'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { InstallmentsConfig } from '.'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  NumberInput,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../..'
import { messages } from './messages'
import { validationSuite } from './validation'

interface Props {
  state: UseDrawerStateReturn
  initialConfig: InstallmentsConfig | undefined
  onSave: (config: InstallmentsConfig | undefined) => void
}

const EMPTY_CONFIG = Object.freeze({ min: undefined, max: undefined })

export function Editor(props: Props) {
  const { state, onSave, initialConfig } = props
  const { hide } = state

  const isCreating = initialConfig === undefined
  const intl = useIntl()
  const { $t } = intl

  const config = useMemo(
    () => initialConfig ?? { ...EMPTY_CONFIG },
    [initialConfig]
  )

  const { drawerForm } = useDrawerSubForm<NonNullable<InstallmentsConfig>>({
    config,
    isOpen: state.open,
    validationFn: validationSuite(intl),
  })

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()

    if (isValid) {
      const values = drawerForm.getValues()
      const hasInstallments = values.min || values.max
      onSave(hasInstallments ? values : undefined)
      hide()
    }
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>{$t(messages.editor.description)}</DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Flex className={csx({ columnGap: '$space-5', '>div': { flex: 1 } })}>
          <NumberInput
            name="min"
            label={$t(messages.editor.min)}
            state={drawerForm}
            fractionDigits={0}
          />
          <NumberInput
            name="max"
            label={$t(messages.editor.max)}
            state={drawerForm}
            fractionDigits={0}
          />
        </Flex>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
